import { IAlbum } from 'ngx-lightbox';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import ProductHelper from 'src/app/helpers/product-helper';
import { StringHelper } from 'src/app/helpers/string-helper';
import Board from 'src/app/model/product/board/board';
import BoardPart from 'src/app/model/product/board/board-part';
import BoardPlacement from 'src/app/model/product/board/board-placement';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import Product from 'src/app/model/product/product';
import { ProductTypes } from 'src/app/model/product/product-type';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import { Adjustment } from 'src/app/model/project/adjustment';
import { SUB_PROJECT_TYPES } from 'src/app/model/project/sub-project';
import { LoadTask } from 'src/app/model/task-list/load-task';
import { FormatPhoneNumber } from 'src/app/modules/app-pipes/format-phonenumber';
import SubProductDisplayHelper from '../../shared-helper/sub-product-display-helper';
import ProductDisplay from './product-display';
import AdjustmentDisplay, { AdjustmentDisplayItem } from './adjustment-display';
import ProjectHelper from 'src/app/helpers/project-helper';
import ProductAsset from 'src/app/model/product-asset';
import {
  ConstructionMaterialLoadEntry,
  OfflineTaskChangesObject,
  StaticLoadEntry,
} from 'src/app/services/placer-offline-changes/task/offline-storage/offline-task-changes-object';
import BoardConstructionMaterial from 'src/app/model/product/board/board-construction-materials';
import GeneralPlacerFormHelper from '../../shared-helper/general-placer-form-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';

export default class LoadTaskPlacerFormHelper {
  private static ADJUSTMENT_DESIGN_IMPRESSION_ASSETS = [
    'adjustmentDesign',
    'adjustmentInternal',
  ];

  public static getProductDisplay(
    product: Product,
    context: LoadTaskPlacerFormContext
  ): ProductDisplay {
    const designAlbum = this.getDesignAlbum(product);
    const sketchUpAlbum = GeneralPlacerFormHelper.getSketchupAlbum(product);
    return {
      id: product.id,
      productType: ProductHelper.getTypeOf(product),
      productTypeValue: product.type,
      materialLine: this.getMaterialLine(product),
      subProductDisplays: SubProductDisplayHelper.getSubProductDisplays(
        product,
        false
      ),
      title: product.title,
      text: product.text,
      productPhoneNumbers: this.getProductPhoneNumbers(
        product,
        context.formatPhoneNumber
      ),
      album: designAlbum,
      sketchUpAlbum: sketchUpAlbum,
      paintedPostsTexts: this.getPaintedPostsTexts(product),
      newWoodPosts: this.getNewWoodPosts(product),
    };
  }

  private static getDesignAlbum(product: Product): IAlbum[] {
    return !CollectionHelper.isPopulated(product.productAssets)
      ? null
      : product.productAssets
          .filter((pa) => !pa.archived && ProductHelper.isDesignAsset(pa))
          .map((pa) => {
            return {
              src: 'api/assets/' + pa.asset.uuid,
              caption: pa.formSection,
              thumb: 'api/assets/' + pa.asset.uuid + '/30',
              preview: 'api/assets/' + pa.asset.uuid + '/177/100',
            };
          });
  }

  private static getMaterialLine(product: Product): string {
    switch (product.type) {
      case ProductTypes.WINDOWSTICKER.value:
      case ProductTypes.CUSTOMWINDOWSTICKER.value:
        const windowSticker = <Windowsticker>product;
        return StringHelper.join(
          [
            windowSticker.temporaryMaterial ? 'Tijdelijk' : 'Permanent',
            windowSticker.foil == null ? null : windowSticker.foil.name,
            windowSticker.laminate == null ? null : windowSticker.laminate.name,
          ],
          ' - '
        );
      case ProductTypes.BOARD.value:
        return null;
      case ProductTypes.OTHER_PRODUCT.value:
        const otherProduct = <OtherProduct>product;
        return otherProduct.otherProductMaterial;
      default:
        return null;
    }
  }

  private static getProductPhoneNumbers(
    product: Product,
    formatPhoneNumber: FormatPhoneNumber
  ): string[] {
    return CollectionHelper.isPopulated(product.productPhoneNumbers)
      ? product.productPhoneNumbers
          .filter(
            (ppn) =>
              !StringHelper.isEmpty(ppn.phoneNumbers) &&
              ppn.phoneNumbers !== 'Geen'
          )
          .map((ppn) => formatPhoneNumber.transform(ppn.phoneNumbers))
      : null;
  }

  public static getExtraInfoNeedMaterialsProduct(product: Product): string {
    return product.productPlacement != null &&
      product.productPlacement.needExtraMaterials
      ? StringHelper.isEmpty(
          product.productPlacement.extraInfoNeedExtraMaterials
        )
        ? 'Extra materialen nodig'
        : product.productPlacement.extraInfoNeedExtraMaterials
      : null;
  }

  public static getExtraInfoNeedMaterialsAdjustment(
    adjustment: Adjustment
  ): string {
    return adjustment.needExtraMaterials
      ? StringHelper.isEmpty(adjustment.extraInfoNeedExtraMaterials)
        ? 'Extra materialen nodig'
        : adjustment.extraInfoNeedExtraMaterials
      : null;
  }

  private static getPaintedPostsTexts(product: Product): string[] {
    return product.type !== ProductTypes.BOARD.value ||
      !CollectionHelper.isPopulated((<Board>product).boardParts)
      ? null
      : (<Board>product).boardParts
          .map((bp, i) =>
            this.getPaintedPostsText(bp, i, (<Board>product).boardParts.length)
          )
          .filter((line) => line != null);
  }

  private static getPaintedPostsText(
    boardPart: BoardPart,
    index: number,
    amountParts: number
  ): string {
    const line = ProductHelper.calculatePaintedPostsDisplay(boardPart);
    return line == null
      ? null
      : amountParts === 1
      ? line
      : `Deelbord ${index + 1}: ${line}`;
  }

  private static getNewWoodPosts(product: Product): string {
    if (product.type !== ProductTypes.BOARD.value) {
      return null;
    }
    const boardPlacement = <BoardPlacement>(<Board>product).productPlacement;
    return boardPlacement.newWoodPosts == null
      ? null
      : boardPlacement.newWoodPosts
      ? 'nieuw hout'
      : 'recup hout';
  }

  public static getImmoscoopLogo(task: LoadTask): boolean {
    return this.atLeastOneCompanyIsImmoscoop(task) &&
      task.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? task.product.immoscoopLogo
      : (<Adjustment>task.subProject).immoscoopLogo;
  }
  public static getSpottopLogo(task: LoadTask): boolean {
    return this.atLeastOneCompanyIsSpotto(task) &&
      task.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? task.product.spottoLogo
      : (<Adjustment>task.subProject).spottoLogo;
  }

  private static atLeastOneCompanyIsImmoscoop(task: LoadTask): boolean {
    return (
      CollectionHelper.isPopulated(task.subProject.project.projectContacts) &&
      task.subProject.project.projectContacts.some((pc) => pc.company.immoscoop)
    );
  }

  private static atLeastOneCompanyIsSpotto(task: LoadTask): boolean {
    return (
      CollectionHelper.isPopulated(task.subProject.project.projectContacts) &&
      task.subProject.project.projectContacts.some((pc) => pc.company.spotto)
    );
  }

  public static getInpressLogo(task: LoadTask): boolean {
    return task.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT
      ? task.product.inpressLogo
      : (<Adjustment>task.subProject).inpressLogo;
  }

  public static getAdjustmentDisplay(task: LoadTask): AdjustmentDisplay {
    const adjustment = <Adjustment>task.subProject;
    return {
      infoTitle: this.getAdjustmentInfoTitle(adjustment),
      adjustmentInfo: adjustment.extraInfoAdjustment,
      adjustmentDisplayItems: this.getAdjustmentDisplayItems(adjustment),
      album: this.getAdjustmentDesignAlbum(adjustment),
    };
  }

  private static getAdjustmentDisplayItems(
    adjustment: Adjustment
  ): AdjustmentDisplayItem[] {
    return [
      this.getAdjustmentMaterialDisplayItem(adjustment),
      this.getAdjustmentFormatDisplayItem(adjustment),
      this.getAdjustmentAmountDisplayItem(adjustment),
    ].filter((e) => e != null);
  }

  private static getAdjustmentInfoTitle(adjustment: Adjustment): string {
    return `${ProjectHelper.getSubProjectTypeTitle(adjustment)} info`;
  }

  private static getAdjustmentMaterialDisplayItem(
    adjustment: Adjustment
  ): AdjustmentDisplayItem {
    return adjustment.material == null
      ? null
      : { header: 'Materiaal', line: adjustment.material };
  }

  private static getAdjustmentFormatDisplayItem(
    adjustment: Adjustment
  ): AdjustmentDisplayItem {
    return adjustment.size == null
      ? null
      : { header: 'Afmetingen', line: adjustment.size };
  }

  private static getAdjustmentAmountDisplayItem(
    adjustment: Adjustment
  ): AdjustmentDisplayItem {
    return adjustment.amountDesign == null
      ? null
      : { header: 'Aantal', line: adjustment.amountDesign };
  }

  private static getAdjustmentDesignAlbum(adjustment: Adjustment): IAlbum[] {
    return adjustment.projectPlacement == null ||
      !CollectionHelper.isPopulated(
        adjustment.projectPlacement.projectPlacementAssets
      )
      ? null
      : adjustment.projectPlacement.projectPlacementAssets
          .filter((pa) => !pa.archived && this.isAdjustmentDesignAsset(pa))
          .map((pa) => {
            return {
              src: 'api/assets/' + pa.asset.uuid,
              caption: pa.formSection,
              thumb: 'api/assets/' + pa.asset.uuid + '/30',
              preview: 'api/assets/' + pa.asset.uuid + '/177/100',
            };
          });
  }

  private static isAdjustmentDesignAsset(productAsset: ProductAsset): boolean {
    return (
      productAsset != null &&
      this.ADJUSTMENT_DESIGN_IMPRESSION_ASSETS.includes(
        productAsset.formSection
      )
    );
  }

  public static initializeLoadListItems(
    context: LoadListEntryContext
  ): LoadList {
    const loadList: LoadList = {};

    this.initializeImmoscoopAndOrSpottoAndOrInpressLoadEntry(context, loadList);

    if (context.loadTask.subProject.type === SUB_PROJECT_TYPES.BASE_PROJECT) {
      this.initializeBaseProjectLoadListItems(context, loadList);
    } else {
      this.initializeAdjustmentLoadListItems(context, loadList);
    }

    return loadList;
  }

  private static initializeImmoscoopAndOrSpottoAndOrInpressLoadEntry(
    context: LoadListEntryContext,
    loadList: LoadList
  ): void {
    if (context.immoscoopLogo || context.spottoLogo || context.inpressLogo) {
      let entry = context.taskChanges.immoscoopAndOrSpottoAndOrInpressLoadEntry;
      const header =
        StringHelper.join(
          [
            context.immoscoopLogo ? 'Immoscoop' : null,
            context.spottoLogo ? 'Spotto' : null,
            context.inpressLogo ? 'Inpress' : null,
          ],
          ' / '
        ) + ' label(s)';
      loadList.immoscoopAndOrSpottoAndOrInpressLoadEntry =
        entry == null
          ? {
              type: 'immoscoop-and-or-spotto-and-or-inpress',
              header: header,
              label: null,
              loaded: GeneralHelper.falseIfNull(context.loadTask.complete),
            }
          : entry;
      loadList.immoscoopAndOrSpottoAndOrInpressLoadEntry.header = header; // ensure latest information is shown in form
    }
  }

  private static initializeBaseProjectLoadListItems(
    context: LoadListEntryContext,
    loadList: LoadList
  ): void {
    const board = <Board>context.loadTask.product;

    if (!StringHelper.isEmpty(board.tilingFormats)) {
      let tilingEntry = context.taskChanges.tilingLoadEntry;
      loadList.tilingLoadEntry =
        tilingEntry == null
          ? {
              type: 'tiling',
              header: 'Borden',
              label: board.tilingFormats,
              loaded: GeneralHelper.falseIfNull(context.loadTask.complete),
            }
          : tilingEntry;
      loadList.tilingLoadEntry.label = board.tilingFormats; // ensure latest information is shown in form
      loadList.tilingLoadEntry.header = 'Borden';
    }

    const materialLoadEntries: ConstructionMaterialLoadEntry[] = [];
    loadList.materialLoadEntries = materialLoadEntries;

    this.initializeConstructionLoadListEntries(
      GeneralHelper.falseIfNull(context.loadTask.complete),
      board.boardConstructionMaterials,
      context.taskChanges,
      materialLoadEntries
    );
  }

  private static initializeAdjustmentLoadListItems(
    context: LoadListEntryContext,
    loadList: LoadList
  ): void {
    const adjustment = <Adjustment>context.loadTask.subProject;

    if (
      !StringHelper.isEmpty(adjustment.material) ||
      !StringHelper.isEmpty(adjustment.amountDesign)
    ) {
      const label = StringHelper.join(
        [adjustment.material, adjustment.amountDesign],
        ' / '
      );
      let tilingEntry = context.taskChanges.tilingLoadEntry;
      loadList.tilingLoadEntry =
        tilingEntry == null
          ? {
              type: 'tiling',
              header: 'Materiaal',
              label: label,
              loaded: GeneralHelper.falseIfNull(context.loadTask.complete),
            }
          : tilingEntry;
      loadList.tilingLoadEntry.label = label; // ensure latest information is shown in form
      loadList.tilingLoadEntry.header = 'Materiaal';
    }
  }

  private static initializeConstructionLoadListEntries(
    taskComplete: boolean,
    constructionMaterials: BoardConstructionMaterial[],
    taskChanges: OfflineTaskChangesObject,
    loadEntries: ConstructionMaterialLoadEntry[]
  ): void {
    if (CollectionHelper.isPopulated(constructionMaterials)) {
      this.initializeConstructionLoadListEntriesFromOffline(
        constructionMaterials,
        taskChanges,
        loadEntries
      );
      this.initializeRemainingConstructionLoadListEntries(
        taskComplete,
        constructionMaterials,
        loadEntries
      );
      loadEntries.sort((e1, e2) => e1.id - e2.id);
    }
  }

  private static initializeConstructionLoadListEntriesFromOffline(
    constructionMaterials: BoardConstructionMaterial[],
    taskChanges: OfflineTaskChangesObject,
    loadEntries: ConstructionMaterialLoadEntry[]
  ): void {
    if (CollectionHelper.isPopulated(taskChanges.materialLoadEntries)) {
      const idToMaterial = CollectionHelper.toIdMap(
        taskChanges.materialLoadEntries
      );
      constructionMaterials.forEach((m) => {
        const offlineEntry = idToMaterial.get(m.id);
        const label =
          m.signConstructionMaterialType == null
            ? null
            : m.signConstructionMaterialType.label;
        if (offlineEntry != null) {
          const loadEntry: ConstructionMaterialLoadEntry = {
            id: m.id,
            label: label,
            length: m.signConstructionMaterialLength,
            description: m.description,
            amount: m.amount,
            loaded: offlineEntry.loaded,
          };
          loadEntries.push(loadEntry);
        }
      });
    }
  }

  private static initializeRemainingConstructionLoadListEntries(
    taskComplete: boolean,
    constructionMaterials: BoardConstructionMaterial[],
    loadEntries: ConstructionMaterialLoadEntry[]
  ): void {
    const idToEntry = CollectionHelper.toNonOptionalIdMap(loadEntries);
    constructionMaterials.forEach((m) => {
      const loadEntry = idToEntry.get(m.id);
      const label =
        m.signConstructionMaterialType == null
          ? null
          : m.signConstructionMaterialType.label;
      if (loadEntry == null) {
        const newEntry: ConstructionMaterialLoadEntry = {
          id: m.id,
          label: label,
          length: m.signConstructionMaterialLength,
          description: m.description,
          amount: m.amount,
          loaded: taskComplete,
        };
        loadEntries.push(newEntry);
      }
    });
  }

  public static isEverythingLoaded(loadList: LoadList): boolean {
    return (
      (loadList.tilingLoadEntry == null || loadList.tilingLoadEntry.loaded) &&
      (loadList.immoscoopAndOrSpottoAndOrInpressLoadEntry == null ||
        loadList.immoscoopAndOrSpottoAndOrInpressLoadEntry.loaded) &&
      (!CollectionHelper.isPopulated(loadList.materialLoadEntries) ||
        loadList.materialLoadEntries.every((m) => m.loaded))
    );
  }
}

export interface LoadTaskPlacerFormContext {
  formatPhoneNumber: FormatPhoneNumber;
}

export interface LoadListEntryContext {
  loadTask: LoadTask;
  taskChanges: OfflineTaskChangesObject;
  immoscoopLogo: boolean;
  spottoLogo: boolean;
  inpressLogo: boolean;
}

export interface LoadList {
  tilingLoadEntry?: StaticLoadEntry;
  immoscoopAndOrSpottoAndOrInpressLoadEntry?: StaticLoadEntry;
  materialLoadEntries?: ConstructionMaterialLoadEntry[];
}
